import DailyChallenge from "../games/DailyChallenge.js";
import { PICTO_FLAME, PICTO_REVIEW, PICTO_TROPHY, PICTO_CHECK, PICTO_PLAY } from "../utils/Pictos.js";
import { isStreakAlive } from "../utils/StreakRules.js";
import { themeFor } from "../utils/DailyQuestions.js";
import { GAME_GROUPS } from "../utils/gameGroups.js";
import StorageService from "../services/StorageService.js";
import InProgress from "./InProgress.js";
import { renderInstallCard } from "../services/InstallPrompt.js";
import * as ReviewDeck from "../utils/ReviewDeck.js";

/** Icône du jeu dans le bandeau « Ta partie en cours » (celles du classement, plus le Lab). */
const RESUME_ICONS = {
	...Object.fromEntries(GAME_GROUPS.map((g) => [g.nameKey, g.icon])),
	"lab.shapeNerd": "/img/lab-shape.svg",
};

/** Couleur du mode, la même que sur les pilules des cartes (Classic bleu, Hard rouge…). */
const RESUME_TONES = {
	"home.classic": "classic",
	"home.hard": "hard",
	"home.speedRun": "speed-run",
	"home.tricky": "tricky",
	"home.reverse": "reverse",
	"home.list": "list",
};

/**
 * Garde-fou de la prise de relais de l'écran d'apparition, pour un navigateur
 * sans `getAnimations()`. Volontairement très au-delà de la fin de la séquence
 * CSS (~1,25 s) : la synchro réelle passe par la promesse `finished` du
 * décalage, et déclencher le fondu du voile trop tôt laisserait voir le logo
 * encore en course, rogné par le bord de l'écran.
 */
const SPLASH_HANDOFF_FALLBACK_MS = 3000;

/** Durée du fondu de sortie du voile (`$splash-out-duration` dans _home-splash.scss). */
const SPLASH_FADE_MS = 400;

export default class HomeCards {
	constructor() {
		this.splash = document.querySelector(".home-splash");
		// Désarme le filet de sécurité CSS du voile : à partir d'ici c'est ce module
		// qui pilote sa sortie (cf. `:not(.js-armed)` dans _home-splash.scss).
		if (this.splash) this.splash.classList.add("js-armed");

		this.cards = document.querySelectorAll(".game-card.has-levels");
		this.cards.forEach(card => {
			// Hidden second step (mode links) starts out of the tab order.
			this.setFlipped(card, false);

			card.querySelector(".card-first-step").addEventListener("click", () => {
				this.closeCards();
				card.classList.add("show-second-step");
				this.setFlipped(card, true);
			});
		});

		this.renderDailyStreakBadge();

		// Badge is in the DOM now, so the intro can stagger it in with the cards.
		this.startIntro();

		// Back-forward cache safety net. If the user clicks a card link *during* the
		// intro and later hits Back, the browser restores this page from bfcache with
		// the staggered elements frozen mid-animation at the GSAP `from()` opacity:0.
		// DOMContentLoaded does not re-fire on a bfcache restore, so pageshow(persisted)
		// is the only hook to force the visible end-state. (The page container itself
		// is reset separately by PageTransition.)
		window.addEventListener("pageshow", (event) => {
			if (event.persisted) this.revealAll();
		});
	}

	/**
	 * Force the intro's visible end-state regardless of where GSAP left off.
	 * Drops the `intro-pending` gate, kills the timeline, and clears the inline
	 * opacity/transform GSAP set on the staged elements so they revert to their
	 * natural (visible) CSS state.
	 */
	revealAll() {
		const page = document.querySelector(".page.home");
		if (!page) return;

		if (this.splash) {
			this.splash.remove();
			this.splash = null;
		}

		if (this.introTimeline) this.introTimeline.kill();
		page.classList.remove("intro-pending");

		page.querySelectorAll(
			".main-title small, .main-title span, .home-intro, .game-card, .daily-streak-badge"
		).forEach(el => {
			el.style.opacity = "";
			el.style.transform = "";
		});

		const footer = document.querySelector("footer");
		if (footer) {
			footer.style.opacity = "";
			footer.style.transform = "";
		}
	}

	/**
	 * Passe le relais de l'écran d'apparition (`.home-splash`) à l'intro de la home.
	 *
	 * L'animation du logo est purement CSS (voir `_home-splash.scss`) : elle démarre
	 * au premier paint, sans attendre ce bundle ni GSAP. Ici on attend la fin réelle
	 * de son décalage, puis on lance d'un même geste l'intro séquencée et le fondu
	 * du voile — les cartes montent donc pendant que le bleu s'efface.
	 *
	 * C'est bien la fin RÉELLE qu'il faut attendre, pas un délai calculé : le voile
	 * et le logo n'ont pas la même horloge d'animation pendant le chargement, et
	 * effacer le voile trop tôt laisse voir le logo encore en course, sa partie
	 * droite rognée par le bord de l'écran.
	 */
	startIntro() {
		const logo = this.splash && this.splash.querySelector(".home-splash-logo");
		const reduceMotion = window.matchMedia
			&& window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		// Pas de splash (autre page, markup absent) ou mouvement réduit : on révèle
		// la home tout de suite. `playIntro()` gère lui aussi le cas reduced-motion.
		if (!logo || reduceMotion) {
			if (this.splash) {
				this.splash.remove();
				this.splash = null;
			}
			this.playIntro();
			return;
		}

		let handed = false;
		const handoff = () => {
			if (handed) return;
			handed = true;
			this.leaveSplash();
			this.playIntro();
		};

		// `getAnimations()` couvre les deux cas : séquence encore en cours, ou déjà
		// terminée avant l'arrivée du bundle (une écoute `animationend` posée trop
		// tard ne se déclencherait jamais, et la home resterait cachée). Les
		// animations en `fill-mode: both` restent listées après leur fin, donc leur
		// promesse `finished` est déjà résolue dans ce second cas.
		// `subtree` : on attend AUSSI le scale du logo et la révélation de "NERD",
		// pas seulement le décalage du conteneur.
		const anims = typeof logo.getAnimations === "function"
			? logo.getAnimations({ subtree: true })
			: [];
		if (anims.length) {
			Promise.all(anims.map(a => a.finished.catch(() => {}))).then(handoff);
		}
		// Garde-fou : navigateur sans `getAnimations()`, ou animation annulée.
		setTimeout(handoff, SPLASH_HANDOFF_FALLBACK_MS);
	}

	/**
	 * Lance le fondu de sortie du voile, puis le retire du DOM pour ne pas laisser
	 * une couche plein écran (même invisible) au-dessus de la page.
	 */
	leaveSplash() {
		const splash = this.splash;
		if (!splash) return;
		this.splash = null;

		// Fige le logo à sa position/largeur réelles (en px) avant de déclencher le
		// fondu : à cet instant précis, certains mobiles recalculent brièvement en
		// double taille le viewport de positionnement `fixed`, ce qui fait "sauter"
		// tout élément encore centré par le flex du voile — visible pile au moment où
		// on lit sa position. `clientWidth`/`clientHeight`, eux, ne dérivent pas : on
		// recalcule le centrage à la main à partir d'eux plutôt que de faire confiance
		// à `rect.left/top` (qui refléterait, lui, le centrage déjà faussé).
		const logo = splash.querySelector(".home-splash-logo");
		if (logo) {
			const rect = logo.getBoundingClientRect();
			const left = (document.documentElement.clientWidth - rect.width) / 2;
			const top = (document.documentElement.clientHeight - rect.height) / 2;
			logo.style.position = "fixed";
			logo.style.left = `${left}px`;
			logo.style.top = `${top}px`;
			logo.style.width = `${rect.width}px`;
			logo.style.margin = "0";
		}

		splash.classList.add("is-leaving");
		// Timeout plutôt que `transitionend` : sur un onglet en arrière-plan la
		// transition est gelée et l'événement ne viendrait jamais, laissant le nœud
		// en place indéfiniment.
		setTimeout(() => splash.remove(), SPLASH_FADE_MS + 200);
	}

	/**
	 * Sequenced entrance for the home page: title → cards → badge → footer.
	 *
	 * The header does NOT take part: c'est la barre de navigation de toute page,
	 * pas un élément décoratif de la home. La faire disparaître puis réapparaître
	 * en fondu la rendait illisible une fraction de seconde (texte clair sur son
	 * propre fond encore translucide) — une barre de nav reste stable dès le
	 * premier paint, ici comme ailleurs.
	 *
	 * The `.intro-pending` gate (set in the HTML, styled in _home.scss) keeps the
	 * animated elements hidden from the first paint. We remove the gate *before*
	 * building the timeline so GSAP captures the visible end-state; `gsap.from()`'s
	 * immediateRender then re-hides each element inline in the same synchronous
	 * frame — so there's no flash despite dropping the CSS gate first.
	 */
	playIntro() {
		const page = document.querySelector(".page.home");
		if (!page) return;

		const reduceMotion = window.matchMedia
			&& window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		// No GSAP (bundle/script issue) or reduced-motion preference → reveal at
		// once, no animation. This is also the safety net that guarantees content
		// never stays stuck behind the gate.
		if (reduceMotion || typeof gsap === "undefined") {
			page.classList.remove("intro-pending");
			return;
		}

		const footer = document.querySelector("footer");
		const titleParts = page.querySelectorAll(".main-title small, .main-title span, .home-intro");
		const cards = page.querySelectorAll(".game-card");
		const badge = page.querySelector(".daily-streak-badge");

		page.classList.remove("intro-pending");

		const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
		this.introTimeline = tl;

		tl.from(titleParts, {
			y: 22, opacity: 0, duration: 0.6, stagger: 0.12, clearProps: "transform,opacity"
		}, 0.12);
		tl.from(cards, {
			y: 48, scale: 0.9, opacity: 0, duration: 0.6, stagger: 0.09,
			ease: "back.out(1.4)", clearProps: "transform,opacity"
		}, 0.3);
		if (badge) {
			tl.from(badge, { scale: 0, opacity: 0, duration: 0.45, ease: "back.out(2.2)" }, ">-0.15");
		}
		// Masqué pendant l'attente (cf. la porte `:has()` dans _home.scss) : il
		// rentre en dernier, une fois les cartes en place, pour ne pas attirer
		// l'œil en bas de page pendant le stagger.
		if (footer) {
			tl.from(footer, { opacity: 0, duration: 0.45, clearProps: "opacity" }, 0.75);
		}
	}

	/**
	 * Surface a 🔥 streak badge on the Daily Challenge card. The card links to both
	 * the classic and hard dailies, so we show the strongest streak that is still
	 * alive today (re-validated against today's day index, since a streak that
	 * broke yesterday is only reset the next time that mode is finished).
	 */
	renderDailyStreakBadge() {
		const card = document.querySelector(".daily-challenge-card");
		if (!card) return;

		const dayNumber = DailyChallenge.dayNumberFor(DailyChallenge.todayStr());
		const best = ["geonerd.daily", "geonerd.daily.hard"]
			.map(prefix => this.readLiveStreak(`${prefix}.streak`, dayNumber))
			.reduce((max, current) => Math.max(max, current), 0);
		if (best < 1) return;

		const badge = document.createElement("div");
		badge.className = "daily-streak-badge";
		badge.setAttribute("aria-hidden", "true");
		badge.innerHTML = `${PICTO_FLAME}${best}`;
		card.appendChild(badge);
	}

	/**
	 * Accueil personnalisé (19 du TODO), une fois les données pays chargées et la
	 * sauvegarde du compte fusionnée : reprendre la partie en cours, thème et
	 * résultat du Défi du jour, record de chaque mode sur sa pilule. Tout part du
	 * localStorage : un nouveau venu voit la home habituelle.
	 */
	renderPersonal() {
		this.renderResume();
		this.renderDailyStatus();
		this.renderModeBests();
		this.renderReviewChip();
		this.renderInstall();
	}

	/** « N pays à réviser » : les erreurs du joueur reviennent, c'est une bonne raison de passer. */
	renderReviewChip() {
		const cards = document.querySelector(".page.home .game-cards");
		if (!cards || document.querySelector(".home-review")) return;
		const due = ReviewDeck.stats().due;
		if (due < 1) return;
		const slug = window.geoNerdApp.languageManager.currentLang === "en" ? "review" : "revision";
		const label = this.t(due === 1 ? "home.reviewDueOne" : "home.reviewDue").replace("{n}", due);
		cards.insertAdjacentHTML("beforebegin", `<a class="home-review" href="${this.href(slug)}">${PICTO_REVIEW} ${label}</a>`);
	}

	/** Invite d'installation pour un joueur qui revient ; l'événement du navigateur peut arriver après. */
	renderInstall() {
		const cards = document.querySelector(".page.home .game-cards");
		if (!cards) return;
		renderInstallCard(cards);
		if (!this.installListener) {
			this.installListener = () => renderInstallCard(cards);
			document.addEventListener("geonerd:installable", this.installListener);
		}
	}

	t(key) {
		return window.geoNerdApp.languageManager.t(key);
	}

	href(slug) {
		const prefix = window.geoNerdApp.languageManager.currentLang === "en" ? "/en" : "";
		return `${prefix}/${slug}`;
	}

	/**
	 * La partie commencée le plus récemment, en carte cliquable : icône du jeu,
	 * mode, barre d'avancement et bouton « Reprendre ». Les autres parties en
	 * cours tiennent dans une pastille qui mène à /parties-en-cours.
	 */
	renderResume() {
		const cards = document.querySelector(".page.home .game-cards");
		if (!cards || document.querySelector(".home-resume")) return;
		let runs = [];
		try {
			runs = InProgress.scanAll();
		} catch (e) {
			return;
		}
		if (!runs.length) return;

		const latest = [...runs].sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0))[0];
		const { mode } = latest;
		const scope = latest.letter ? ` (${latest.letter.toUpperCase()})` : mode.scopeKey ? ` (${this.t(mode.scopeKey)})` : "";
		const icon = RESUME_ICONS[mode.gameNameKey] || "/img/game.svg";
		const tone = RESUME_TONES[mode.modeKey] || "classic";
		const percent = latest.total ? Math.round((latest.found / latest.total) * 100) : 0;
		const othersCount = runs.length - 1;
		const inProgressSlug = window.geoNerdApp.languageManager.currentLang === "en" ? "games-in-progress" : "parties-en-cours";
		const others = othersCount > 0 ? `
			<a class="home-resume-others" href="${this.href(inProgressSlug)}">
				<span class="home-resume-others-count">+${othersCount}</span>
				<span>${this.t(othersCount === 1 ? "home.resumeOthersOne" : "home.resumeOthers")}</span>
				<span class="home-resume-others-arrow" aria-hidden="true">→</span>
			</a>` : "";

		cards.insertAdjacentHTML("beforebegin", `
			<div class="home-resume">
				<a class="home-resume-card tone-${tone}" href="${this.href(mode.slug)}${mode.query || ""}">
					<span class="home-resume-icon"><img src="${icon}" alt="" width="28" height="28"></span>
					<span class="home-resume-text">
						<span class="home-resume-title">${this.t("home.resumeTitle")}</span>
						<span class="home-resume-game">${this.t(mode.gameNameKey)} <span class="home-resume-mode">${this.t(mode.modeKey)}${scope}</span></span>
						<span class="home-resume-progress">
							<span class="home-resume-bar"><span style="width: ${percent}%"></span></span>
							<span class="home-resume-count">${latest.found}/${latest.total}</span>
						</span>
					</span>
					<span class="home-resume-btn">${PICTO_PLAY} ${this.t("inProgress.resume")}</span>
				</a>
				${others}
			</div>`);
	}

	/** Thème du jour sur la carte du Défi, et score du jour sur la pilule d'un défi déjà joué. */
	renderDailyStatus() {
		const card = document.querySelector(".daily-challenge-card");
		if (!card) return;
		const today = DailyChallenge.todayStr();
		const theme = themeFor(today);
		const subtitle = card.querySelector(".card-first-step .subtitle");
		if (subtitle && !card.querySelector(".daily-card-theme")) {
			const label = this.t("daily.themeToday").replace("{theme}", this.t(`daily.theme.${theme}`));
			subtitle.insertAdjacentHTML("afterend", `<p class="daily-card-theme">${label}</p>`);
		}

		const pills = { classic: card.querySelector(".card-mode-link.classic"), hard: card.querySelector(".card-mode-link.hard") };
		Object.entries(pills).forEach(([mode, pill]) => {
			if (!pill || pill.querySelector(".mode-best")) return;
			try {
				const result = JSON.parse(localStorage.getItem(`${DailyChallenge.STORAGE_PREFIXES[mode]}.${today}`));
				if (result && typeof result.score === "number") {
					pill.insertAdjacentHTML("beforeend", `<span class="mode-best is-done">${PICTO_CHECK} ${result.score}/${result.total || 20}</span>`);
				}
			} catch (e) { /* résultat illisible : pas de badge */ }
		});
	}

	/** Record personnel sur chaque pilule de mode (hors Défi du jour et Country Nerd). */
	renderModeBests() {
		const modes = GAME_GROUPS.flatMap(g => g.modes).filter(m => m.slug && (m.type === "classic" || m.type === "speedrun"));
		document.querySelectorAll(".page.home .card-mode-link").forEach(pill => {
			if (pill.querySelector(".mode-best")) return;
			const slug = (pill.getAttribute("href") || "").replace(/^\/(en\/)?/, "");
			const mode = modes.find(m => m.slug === slug);
			if (!mode) return;
			const best = parseInt(StorageService.load(`${mode.id}.best`, 0), 10) || 0;
			if (best <= 0) return;
			const value = mode.type === "speedrun" ? `${best}` : `${Math.min(best, mode.total)}/${mode.total}`;
			pill.insertAdjacentHTML("beforeend", `<span class="mode-best" title="${this.t("game.best")}">${PICTO_TROPHY} ${value}</span>`);
		});
	}

	/** Current streak count for a key, or 0 if missing/broken (see StreakRules: the joker can still save it). */
	readLiveStreak(key, dayNumber) {
		try {
			const raw = localStorage.getItem(key);
			if (!raw) return 0;
			const s = JSON.parse(raw);
			return isStreakAlive(s, dayNumber) ? (s.current || 0) : 0;
		} catch (e) {
			return 0;
		}
	}

	closeCards() {
		this.cards.forEach(card => {
			card.classList.remove("show-second-step");
			this.setFlipped(card, false);
		});
	}

	/**
	 * Keep keyboard focus in sync with what's visible: the hidden step is `inert`
	 * (the flip only fades opacity + disables pointer-events, which doesn't remove
	 * its links/buttons from the tab order).
	 */
	setFlipped(card, flipped) {
		const first = card.querySelector(".card-first-step");
		const second = card.querySelector(".card-second-step");
		if (first) first.inert = flipped;
		if (second) second.inert = !flipped;
	}
}
